'use client';

import type { ChangeMentoringSubscriptionOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/change-mentoring-subscription/output.dto';
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types';
import { MENTORING_OFFER_EMAIL_DELIVERY, type UpsertMentoringOfferOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/upsert-mentoring-offer/output.dto';
import { PAYMENT__BILLING_CADENCE_DESCRIPTIONS, PAYMENT__MENTORING_TYPE, PAYMENT__MENTORING_TYPE_DESCRIPTIONS, type PaymentMentoringType } from '@algoacademy/backend/modules/payment/enums';
import { PAYMENT__PERMISSIONS } from '@algoacademy/backend/modules/payment/permissions';
import { isTrpcErrorCode, isUserFacingTrpcError } from '@algoacademy/backend/trpc/client-errors';
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { toast } from '@algoacademy/design-system/web/feedback/Toaster/Toaster';
import { showTrpcMutationErrorToast } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { Description, ErrorMessage, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '@/app/_auth/useAuth';
import { useTRPC } from '@/app/_trpc/config';
import { ChangeBlockedNotice } from './_components/ChangeBlockedNotice';
import { ChangeMentoringConfirmAlert, type ChangeMentoringConfirmationMode } from './_components/ChangeMentoringConfirmAlert';
import { ChangeOutcomeSummary } from './_components/ChangeOutcomeSummary';
import { IssuedOfferLink } from './_components/IssuedOfferLink';
import { StudentSubscriptionPanel } from './_components/StudentSubscriptionPanel';
import { calculateRenewalPriceZloty, formatTermsSummary, type MentoringOfferProduct } from './_helpers/mentoring-terms';
import { MENTORING_OFFER_FORM_DEFAULTS, type MentoringOfferFormSchema, mentoringOfferFormSchemaZ } from './schema';

/**
 * The checkout order and the offer describe the same product with the same
 * string, but TypeScript keeps enum members and literal unions apart, so the
 * two vocabularies are matched up explicitly.
 */
const MENTORING_TYPE_BY_ORDER: Record<MentoringOfferProduct['order'], PaymentMentoringType> = {
  [ORDER_TYPE.GROUP_MENTORING]: PAYMENT__MENTORING_TYPE.GROUP_MENTORING,
  [ORDER_TYPE.INDIVIDUAL_MENTORING]: PAYMENT__MENTORING_TYPE.INDIVIDUAL_MENTORING,
  [ORDER_TYPE.PROJECT_MENTORING]: PAYMENT__MENTORING_TYPE.PROJECT_MENTORING
};

/** The lookup only fires once the typed address could belong to somebody. */
const studentEmailLookupZ = z.email().max(254);

/**
 * A refused offer explains itself in one long sentence - which mentoring, which
 * cadence, which buyer, and the offer that already paid. Nobody reads that in
 * the five seconds a toast lives, so the same sentence also lands in a panel
 * that stays until the next attempt. Field-level complaints are excluded: the
 * form already shows those next to the inputs.
 */
function readRefusalMessage(error: unknown): string | null {
  if (!isUserFacingTrpcError(error) || isTrpcErrorCode(error, 'BAD_REQUEST')) {
    return null;
  }
  return error.message;
}
type MentoringOfferFormProps = {
  mentoringProduct: MentoringOfferProduct;
};

/**
 * Staff-only, and one form for both jobs. Whatever the visitor configured above
 * - mentoring type, cadence, hours - either becomes a new offer with a
 * `/mentoring/<id>` link for a student who has no subscription, or the new terms
 * of the subscription a student already has. Which one it is comes from looking
 * the typed student e-mail up, not from a second screen. Nobody without the
 * payments permission sees any of it.
 */
export function MentoringOfferForm({
  mentoringProduct
}: MentoringOfferFormProps) {
  const {
    user
  } = useAuth();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  /**
   * Keeps the just-issued result visible across re-renders. On a later lookup,
   * the query supplies the persisted unpaid link instead.
   */
  const [issuedOffer, setIssuedOffer] = useState<{
    studentEmail: string;
    result: UpsertMentoringOfferOutputDTO;
  } | null>(null);
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  /**
   * Tagged with the address it belongs to, so retyping the e-mail cannot leave
   * one student's outcome sitting under another student's subscription.
   */
  const [changeOutcome, setChangeOutcome] = useState<{
    studentEmail: string;
    result: ChangeMentoringSubscriptionOutputDTO;
  } | null>(null);
  const upsertMentoringOffer = useMutation(trpc.payment.upsertMentoringOffer.mutationOptions());
  const changeMentoringSubscription = useMutation(trpc.payment.changeMentoringSubscription.mutationOptions());
  const form = useForm<MentoringOfferFormSchema>({
    defaultValues: MENTORING_OFFER_FORM_DEFAULTS,
    mode: 'onSubmit',
    resolver: zodResolver(mentoringOfferFormSchemaZ)
  });
  const hasPaymentsPermission = user.hasPermissions([PAYMENT__PERMISSIONS.PAYMENTS.MANAGE.ENABLED]);

  // The backend keys students on the lowercased address; the lookup has to ask
  // about exactly the same string the mutations will write.
  const studentEmail = form.watch('studentEmail').trim().toLowerCase();
  const isStudentEmailValid = studentEmailLookupZ.safeParse(studentEmail).success;
  const mentoringStateQuery = useQuery(trpc.payment.getStudentMentoringState.queryOptions({
    studentEmail
  }, {
    enabled: hasPaymentsPermission && isStudentEmailValid
  }));
  if (!hasPaymentsPermission) {
    return null;
  }
  const mentoringType = MENTORING_TYPE_BY_ORDER[mentoringProduct.order];
  const billingCadence = mentoringProduct.subscription;
  // Hours belong to extended mentoring only - anywhere else the backend refuses them.
  const hoursWithMentor = mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING ? mentoringProduct.hoursWithMentor : undefined;
  const chosenTermsSummary = formatTermsSummary({
    mentoringTypeLabel: PAYMENT__MENTORING_TYPE_DESCRIPTIONS[mentoringType],
    billingCadenceLabel: PAYMENT__BILLING_CADENCE_DESCRIPTIONS[billingCadence],
    hoursWithMentor: hoursWithMentor ?? null
  });
  const mentoringState = mentoringStateQuery.data;
  const current = mentoringState?.current ?? null;
  const termsInForceSummary = current === null ? null : formatTermsSummary({
    mentoringTypeLabel: current.mentoringTypeLabel,
    billingCadenceLabel: current.billingCadenceLabel,
    hoursWithMentor: current.hoursWithMentor
  });
  const isTermsInForce = current !== null && current.mentoringType === mentoringType && current.billingCadence === billingCadence && current.hoursWithMentor === (hoursWithMentor ?? null);
  const isTermsAlreadyScheduled = mentoringState?.scheduledChange !== null && mentoringState?.scheduledChange !== undefined && mentoringState.scheduledChange.mentoringType === mentoringType && mentoringState.scheduledChange.billingCadence === billingCadence && mentoringState.scheduledChange.hoursWithMentor === (hoursWithMentor ?? null);
  const confirmationMode: ChangeMentoringConfirmationMode = mentoringState?.scheduledChange !== null && mentoringState?.scheduledChange !== undefined ? isTermsInForce ? 'takeBack' : isTermsAlreadyScheduled ? 'unchangedScheduled' : 'replace' : isTermsInForce ? 'unchangedInForce' : 'schedule';
  const isLookupPending = isStudentEmailValid && mentoringStateQuery.isPending;
  const isChangeMode = mentoringState?.subscriptionState === 'changeable' && current !== null;
  const isBlocked = (mentoringState?.subscriptionState === 'unpaidRenewal' || mentoringState?.subscriptionState === 'cancelled') && current !== null;
  const shownOutcome = changeOutcome !== null && changeOutcome.studentEmail === studentEmail ? changeOutcome.result : null;
  const submittedOffer = issuedOffer !== null && issuedOffer.studentEmail === studentEmail ? issuedOffer.result : null;
  const shownOffer = submittedOffer === null ? mentoringState?.unpaidOffer === null || mentoringState?.unpaidOffer === undefined ? null : {
    offerUrl: mentoringState.unpaidOffer.offerUrl,
    variant: 'existing' as const
  } : {
    offerUrl: submittedOffer.offerUrl,
    variant: submittedOffer.emailDelivery === MENTORING_OFFER_EMAIL_DELIVERY.SENT ? 'sent' as const : 'emailFailed' as const
  };
  const onSubmit = (values: MentoringOfferFormSchema) => {
    setRefusalMessage(null);
    upsertMentoringOffer.mutate({
      mentoringType,
      billingCadence,
      hoursWithMentor,
      buyerEmail: values.buyerEmail,
      buyerName: values.buyerName,
      studentEmail: values.studentEmail,
      studentName: values.studentName
    }, {
      onSuccess: async result => {
        setIssuedOffer({
          studentEmail,
          result
        });
        if (result.emailDelivery === MENTORING_OFFER_EMAIL_DELIVERY.SENT) {
          toast.success('Oferta gotowa. E-mail z linkiem został wysłany kupującemu.');
        } else {
          toast.error('Oferta gotowa, ale e-mail nie został wysłany', {
            description: 'Link pozostaje dostępny pod formularzem. Przekaż go ręcznie.'
          });
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.payment.getStudentMentoringState.queryKey({
            studentEmail
          })
        });
      },
      onError: error => {
        setRefusalMessage(readRefusalMessage(error));
        showTrpcMutationErrorToast(error);
      }
    });
  };
  const onConfirmChange = () => {
    setRefusalMessage(null);
    changeMentoringSubscription.mutate({
      studentEmail,
      mentoringType,
      billingCadence,
      hoursWithMentor
    }, {
      onSuccess: async result_0 => {
        setIsConfirmOpen(false);
        setChangeOutcome({
          studentEmail,
          result: result_0
        });
        if (result_0.outcome === 'unchanged') {
          toast.info('Ustawienia mentoringu się nie zmieniły.');
        } else {
          toast.success('Zapisano. Sprawdź podsumowanie pod formularzem.');
        }

        // The panel above now describes the old truth - refetch it.
        await queryClient.invalidateQueries({
          queryKey: trpc.payment.getStudentMentoringState.queryKey({
            studentEmail
          })
        });
      },
      onError: error_0 => {
        setIsConfirmOpen(false);
        setRefusalMessage(readRefusalMessage(error_0));
        showTrpcMutationErrorToast(error_0);
      }
    });
  };
  return <div className='flex w-full flex-col gap-4 border-borderPrimary border-t pt-6 text-left'>
      <div className='flex flex-col gap-1'>
        <p className='font-semibold text-contentPrimary'>
          {isChangeMode ? 'Zmień ustawienia mentoringu' : 'Wystaw ofertę'}
        </p>
        <p className='text-contentSecondary text-sm'>{chosenTermsSummary}</p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
        {isChangeMode && current !== null ? <Field>
            <Label>Imię i nazwisko ucznia</Label>
            <Input type='text' value={current.studentName} readOnly aria-readonly='true' />
          </Field> : null}

        {isChangeMode || isBlocked ? null : <Field>
            <Label>Imię i nazwisko ucznia</Label>
            <Input {...form.register('studentName')} type='text' autoComplete='off' />
            <ErrorMessage>{form.formState.errors.studentName?.message}</ErrorMessage>
          </Field>}

        <Field>
          <Label>E-mail ucznia</Label>
          <Input {...form.register('studentEmail')} type='email' autoComplete='off' />
          <Description>
            Po wpisaniu poprawnego adresu sprawdzamy, czy uczeń ma już subskrypcję.
          </Description>
          <ErrorMessage>{form.formState.errors.studentEmail?.message}</ErrorMessage>
        </Field>

        {isLookupPending ? <p role='status' className='text-contentSecondary text-sm'>
            Sprawdzamy subskrypcję ucznia...
          </p> : null}

        {mentoringState !== undefined && current !== null ? <StudentSubscriptionPanel subscriptionState={mentoringState.subscriptionState} current={current} scheduledChange={mentoringState.scheduledChange} /> : null}

        {isChangeMode && current !== null ? <>
            {/* readOnly, not disabled: a disabled input greys its value out, so a
                real name reads as an empty placeholder. Read-only keeps the value
                legible and still refuses every edit. */}
            <Field>
              <Label>Imię i nazwisko kupującego</Label>
              <Input type='text' value={current.buyerName} readOnly aria-readonly='true' />
            </Field>

            <Field>
              <Label>E-mail kupującego</Label>
              <Input type='email' value={current.buyerEmail} readOnly aria-readonly='true' />
            </Field>

            {/* Describes the locked buyer and student fields, not one of them, so it cannot
                be a headless-ui Description - that component only renders inside a single Field. */}
            <p className='text-contentSecondary text-sm'>
              Kupującego i ucznia nie da się tu zmienić - inny płatnik to inna karta, czyli nowa
              oferta i nowy link. Zmieniasz tylko rodzaj mentoringu, okres rozliczeniowy i godziny
              wybrane wyżej.
            </p>

            <Button size='xl' type='button' className='w-full' onClick={() => setIsConfirmOpen(true)} loading={changeMentoringSubscription.isPending}>
              Sprawdź i zapisz zmianę
            </Button>
          </> : null}

        {isBlocked && current !== null ? <ChangeBlockedNotice reason={mentoringState.subscriptionState === 'cancelled' ? 'cancelled' : 'unpaidRenewal'} renewsAt={current.renewsAt} /> : null}

        {isChangeMode || isBlocked ? null : <>
            <Field>
              <Label>Imię i nazwisko kupującego</Label>
              <Input {...form.register('buyerName')} type='text' autoComplete='off' />
              <Description>Zostaw puste, gdy uczeń sam kupuje.</Description>
              <ErrorMessage>{form.formState.errors.buyerName?.message}</ErrorMessage>
            </Field>

            <Field>
              <Label>E-mail kupującego</Label>
              <Input {...form.register('buyerEmail')} type='email' autoComplete='off' />
              <Description>
                Zostaw puste, gdy uczeń sam kupuje - użyjemy adresu ucznia w ofercie i w Stripe.
              </Description>
              <ErrorMessage>{form.formState.errors.buyerEmail?.message}</ErrorMessage>
            </Field>

            <Button size='xl' type='submit' className='w-full' disabled={isLookupPending} loading={upsertMentoringOffer.isPending}>
              Wystaw ofertę i wyślij e-mail
            </Button>
          </>}
      </form>

      {refusalMessage ? <p role='alert' className='rounded-lg border border-borderError bg-backgroundSecondary p-4 text-contentRed text-sm'>
          {refusalMessage}
        </p> : null}

      {shownOutcome !== null ? <ChangeOutcomeSummary outcome={shownOutcome} /> : null}

      {shownOffer ? <IssuedOfferLink offerUrl={shownOffer.offerUrl} variant={shownOffer.variant} /> : null}

      {isChangeMode && current !== null ? <ChangeMentoringConfirmAlert isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} onConfirm={onConfirmChange} isPending={changeMentoringSubscription.isPending} studentName={current.studentName} currentTermsSummary={termsInForceSummary ?? ''} newTermsSummary={chosenTermsSummary} newPriceZloty={confirmationMode === 'unchangedScheduled' && mentoringState.scheduledChange !== null ? mentoringState.scheduledChange.priceGrosze / 100 : calculateRenewalPriceZloty(mentoringProduct)} renewsAt={current.renewsAt} mode={confirmationMode} /> : null}
    </div>;
}